import { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  Tooltip, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Legend, ComposedChart, Line,
} from "recharts";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./RevenuePage.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface RevenueData {
  allTime: number;
  thisMonth: number;
  lastMonth: number;
  outstanding: number;
  revenueByMonth: { month: string; revenue: number }[];
  byRoomType: { roomType: string; revenue: number }[];
  topRooms: { roomId: string; bookings: number; revenue: number }[];
}

const EGP = (n: number) => `EGP ${(n ?? 0).toLocaleString()}`;

const PIE_COLORS = ["#1a3a5c", "#1a3a5c", "#ec4899", "#f59e0b", "#10b981", "#1a3a5c", "#ef4444"];

function StatCard({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="rev-stat-card">
      <p className="rev-stat-label">{label}</p>
      <p className="rev-stat-value" style={accent ? { color: accent } : undefined}>{value}</p>
    </div>
  );
}

const CustomBarTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div className="rev-tooltip">
        <p className="rev-tooltip-label">{label}</p>
        <p className="rev-tooltip-value">{EGP(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

const CustomPieTooltip = ({ active, payload }: any) => {
  if (active && payload?.length) {
    return (
      <div className="rev-tooltip">
        <p className="rev-tooltip-label">{payload[0].name}</p>
        <p className="rev-tooltip-value">{EGP(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

interface ForecastWindow { days: number; label: string; confirmed: number; projected: number; avgPerDay: number; }
interface ForecastData {
  windows: ForecastWindow[];
  weeklyData: { week: string; confirmed: number; projected: number }[];
  avgPerDay: number;
  historicalRevenue: number;
}

const ForecastTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rev-tooltip">
      <p className="rev-tooltip-label">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color, margin: "2px 0", fontSize: 12 }}>
          {p.name}: {EGP(p.value)}
        </p>
      ))}
    </div>
  );
};

export default function RevenuePage() {
  const [data, setData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<"overview" | "forecast">("overview");
  const [forecast, setForecast] = useState<ForecastData | null>(null);
  const [forecastLoading, setForecastLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/dashboard/revenue`)
      .then((res) => setData(res.data))
      .catch(() => setError("Failed to load revenue data"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab !== "forecast" || forecast) return;
    setForecastLoading(true);
    axios
      .get(`${API_BASE}/api/dashboard/revenue/forecast`)
      .then(res => setForecast(res.data))
      .catch(() => {})
      .finally(() => setForecastLoading(false));
  }, [tab]);

  return (
    <DashboardLayout>
      <div className="rev-page">
        {/* Header */}
        <div className="rev-header">
          <div>
            <h1 className="rev-title">Revenue</h1>
            <p className="rev-subtitle">Financial overview and revenue breakdown</p>
          </div>
          <div className="rev-tabs">
            <button className={`rev-tab${tab === "overview" ? " active" : ""}`} onClick={() => setTab("overview")}>Overview</button>
            <button className={`rev-tab${tab === "forecast" ? " active" : ""}`} onClick={() => setTab("forecast")}>Forecast</button>
          </div>
        </div>

        {loading && <div className="rev-loading">Loading revenue data…</div>}
        {error && <div className="rev-error">{error}</div>}

        {/* ── FORECAST TAB ── */}
        {tab === "forecast" && (
          <div>
            {forecastLoading && <div className="rev-loading">Generating forecast…</div>}
            {forecast && (
              <>
                {/* Avg per day */}
                <div className="rev-forecast-hint">
                  Baseline: <strong>{EGP(forecast.avgPerDay)}</strong> avg revenue/day (last 90 days)
                  &nbsp;·&nbsp;Historical: <strong>{EGP(forecast.historicalRevenue)}</strong> over last 90 days
                </div>

                {/* 3-window cards */}
                <div className="rev-forecast-grid">
                  {forecast.windows.map(w => (
                    <div key={w.days} className="rev-forecast-card">
                      <div className="rev-forecast-label">Next {w.days} Days</div>
                      <div className="rev-forecast-confirmed">{EGP(w.confirmed)}<span>confirmed</span></div>
                      <div className="rev-forecast-projected">{EGP(w.projected)}<span>projected</span></div>
                      <div className="rev-forecast-bar">
                        <div
                          className="rev-forecast-bar-fill"
                          style={{ width: `${Math.min(100, (w.confirmed / w.projected) * 100)}%` }}
                        />
                      </div>
                      <div className="rev-forecast-pct">
                        {w.projected > 0 ? Math.round((w.confirmed / w.projected) * 100) : 0}% confirmed
                      </div>
                    </div>
                  ))}
                </div>

                {/* Weekly breakdown chart */}
                <div className="rev-chart-card" style={{ marginTop: 20 }}>
                  <h2 className="rev-chart-title">12-Week Revenue Forecast</h2>
                  <p className="rev-chart-sub">Confirmed bookings vs projected (incl. baseline projection)</p>
                  <div className="rev-chart-wrap">
                    <ResponsiveContainer width="100%" height={280}>
                      <ComposedChart data={forecast.weeklyData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(14,34,64,0.10)" />
                        <XAxis dataKey="week" tick={{ fill: "#475569", fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: "#475569", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                        <Tooltip content={<ForecastTooltip />} />
                        <Legend formatter={val => <span style={{ color: "rgba(242,235,217,0.3)", fontSize: 12 }}>{val}</span>} />
                        <Bar dataKey="confirmed" name="Confirmed" fill="#1a3a5c" radius={[4,4,0,0]} />
                        <Bar dataKey="projected" name="Projected" fill="rgba(14,34,64,0.10)" radius={[4,4,0,0]} />
                        <Line dataKey="projected" name="" stroke="#1a3a5c" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {!loading && !error && data && tab === "overview" && (
          <>
            {/* Stat Cards */}
            <div className="rev-stats-grid">
              <StatCard label="All Time Revenue" value={EGP(data.allTime)} accent="#c9a84c" />
              <StatCard label="This Month" value={EGP(data.thisMonth)} accent="#34d399" />
              <StatCard label="Last Month" value={EGP(data.lastMonth)} />
              <StatCard label="Outstanding" value={EGP(data.outstanding)} accent="#f87171" />
            </div>

            {/* Charts Row */}
            <div className="rev-charts-row">
              {/* Bar Chart */}
              <div className="rev-chart-card">
                <h2 className="rev-chart-title">Revenue by Month</h2>
                <p className="rev-chart-sub">Last 12 months</p>
                <div className="rev-chart-wrap">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.revenueByMonth} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(14,34,64,0.10)" />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: "#475569", fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: "#475569", fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                      />
                      <Tooltip content={<CustomBarTooltip />} />
                      <Bar dataKey="revenue" fill="#1a3a5c" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Pie Chart */}
              <div className="rev-chart-card">
                <h2 className="rev-chart-title">Revenue by Room Type</h2>
                <p className="rev-chart-sub">Distribution breakdown</p>
                <div className="rev-chart-wrap">
                  {data.byRoomType?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie
                          data={data.byRoomType}
                          dataKey="revenue"
                          nameKey="roomType"
                          cx="50%"
                          cy="50%"
                          outerRadius={90}
                          innerRadius={44}
                          paddingAngle={3}
                        >
                          {data.byRoomType.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomPieTooltip />} />
                        <Legend
                          formatter={(val) => <span style={{ color: "rgba(242,235,217,0.3)", fontSize: 12 }}>{val}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="rev-empty-chart">No room type data</div>
                  )}
                </div>
              </div>
            </div>

            {/* Top Rooms Table */}
            <div className="rev-table-card">
              <h2 className="rev-section-title">Top Rooms by Revenue</h2>
              {data.topRooms?.length === 0 ? (
                <div className="rev-empty">No room data available</div>
              ) : (
                <div className="rev-table-wrap">
                  <table className="rev-table">
                    <thead>
                      <tr>
                        <th className="rev-th">#</th>
                        <th className="rev-th">Room</th>
                        <th className="rev-th">Bookings</th>
                        <th className="rev-th rev-th-right">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topRooms.map((r, i) => (
                        <tr key={r.roomId} className="rev-tr">
                          <td className="rev-td rev-td-rank">{i + 1}</td>
                          <td className="rev-td">
                            <span className="rev-room-badge">Room {r.roomId}</span>
                          </td>
                          <td className="rev-td">{r.bookings}</td>
                          <td className="rev-td rev-td-right rev-td-revenue">{EGP(r.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
